import { MaterialInput } from 'app/components';
import { getAuthErrorMessage, UserActions } from 'app/redux/User/actions'
import * as React from 'react'
import { Alert, Button, Col, FormGroup, Row } from 'react-bootstrap'
import { Link } from 'react-router-dom';

interface IRegistrationState {
  firstname: string
  lastname: string
  email: string
  username: string
  password: string
  error: any
  isSubmitting: boolean
  message: string
}

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: 'calc(100vh - 64px)',
    padding: '54px 0 70px',
  },
  shell: {
    background: '#ffffff',
    border: '1px solid rgba(2, 38, 54, 0.08)',
    borderRadius: 8,
    boxShadow: '0 24px 60px rgba(2, 38, 54, 0.14)',
    display: 'flex',
    flexWrap: 'wrap',
    overflow: 'hidden',
  },
  brandPanel: {
    background: 'linear-gradient(135deg, #022636 0%, #062d4e 58%, #0070C0 100%)',
    color: '#ffffff',
    flex: '1 1 280px',
    minHeight: 540,
    padding: '42px 38px',
    position: 'relative',
  },
  brandRule: {
    background: '#00B050',
    borderRadius: 4,
    height: 4,
    marginBottom: 28,
    width: 64,
  },
  eyebrow: {
    color: 'rgba(255, 255, 255, 0.74)',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1.6,
    marginBottom: 18,
    textTransform: 'uppercase',
  },
  title: {
    color: '#ffffff',
    fontSize: 34,
    fontWeight: 300,
    lineHeight: 1.18,
    margin: '0 0 18px',
  },
  brandCopy: {
    color: 'rgba(255, 255, 255, 0.82)',
    fontSize: 15,
    lineHeight: 1.7,
    marginBottom: 34,
    maxWidth: 380,
  },
  steps: {
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.16)',
    borderRadius: 8,
    marginTop: 26,
    padding: 20,
  },
  step: {
    color: 'rgba(255, 255, 255, 0.88)',
    fontSize: 14,
    lineHeight: 1.55,
    margin: '0 0 14px',
  },
  stepLast: {
    color: 'rgba(255, 255, 255, 0.88)',
    fontSize: 14,
    lineHeight: 1.55,
    margin: 0,
  },
  formPanel: {
    flex: '1 1 420px',
    padding: '42px 42px 36px',
  },
  formTitle: {
    color: '#022636',
    fontSize: 26,
    fontWeight: 500,
    margin: '0 0 8px',
  },
  formCopy: {
    color: '#667983',
    fontSize: 15,
    lineHeight: 1.6,
    margin: '0 0 26px',
  },
  inputGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: -8,
    marginRight: -8,
  },
  inputColumn: {
    flex: '1 1 220px',
    paddingLeft: 8,
    paddingRight: 8,
  },
  inputGroup: {
    background: '#f7fafb',
    border: '1px solid #e3ebef',
    borderRadius: 8,
    marginBottom: 18,
    padding: '2px 16px 0',
  },
  submitButton: {
    background: '#00B050',
    border: 'none',
    borderRadius: 4,
    boxShadow: '0 8px 18px rgba(0, 176, 80, 0.28)',
    fontSize: 15,
    fontWeight: 600,
    height: 48,
    marginTop: 6,
    width: '100%',
  },
  footer: {
    borderTop: '1px solid #edf2f4',
    color: '#667983',
    fontSize: 14,
    marginTop: 24,
    paddingTop: 22,
    textAlign: 'center',
  },
  loginLink: {
    color: '#0070C0',
    fontWeight: 700,
  },
}

class Registration extends React.Component<{}, IRegistrationState> {
  state = {
    firstname: '',
    lastname: '',
    email: '',
    username: '',
    password: '',
    error: null,
    isSubmitting: false,
    message: '',
  }

  register(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    this.setState({ error: null, isSubmitting: true, message: '' })

    UserActions.register(this.state.username, this.state.password, this.state.firstname, this.state.lastname, this.state.email)
      .then((action) => {
        const registration = action.registration

        if (registration && !registration.userConfirmed) {
          location.hash = '#/confirm?username=' + encodeURIComponent(registration.username) + '&from=signup'
          return
        }

        location.hash = '#/login'
      }, (error) => {
        this.setState({ error: getAuthErrorMessage(error), isSubmitting: false })
      })
  }

  setPartialState(attr: string, value: any) {
    const updateState = {}
    updateState[attr] = value

    this.state.error = null

    this.setState(updateState)
  }

  render() {
    return (
      <section style={styles.page}>
        <section className="container">
          <Row>
            <Col mdOffset={1} md={10}>
              <div className="animated fadeIn" style={styles.shell}>
                <aside style={styles.brandPanel}>
                  <div style={styles.brandRule} />
                  <div style={styles.eyebrow}>Create Your Access</div>
                  <h1 style={styles.title}>Start with a focused FoundationMark account.</h1>
                  <p style={styles.brandCopy}>
                    Register to request access to private foundation performance tools, reports, and benchmark resources.
                  </p>
                  <div style={styles.steps}>
                    <p style={styles.step}>
                      Complete the form with your account details.
                    </p>
                    <p style={styles.stepLast}>
                      After submitting, check your inbox for a confirmation email.
                    </p>
                  </div>
                </aside>

                <div style={styles.formPanel}>
                  <h2 style={styles.formTitle}>Register</h2>
                  <p style={styles.formCopy}>Create credentials for your FoundationMark portal access.</p>

                  <form name="registrationFrom" onSubmit={(e) => this.register(e)}>
                    <div style={styles.inputGrid}>
                      <div style={styles.inputColumn}>
                        <FormGroup style={styles.inputGroup}>
                          <MaterialInput placeholder="Username"
                            required={true}
                            onChange={(value) => this.setPartialState('username', value)}
                            value={this.state.username} />
                        </FormGroup>
                      </div>
                      <div style={styles.inputColumn}>
                        <FormGroup style={styles.inputGroup}>
                          <MaterialInput placeholder="Password (at least 6 characters)"
                            required={true}
                            type="password"
                            onChange={(value) => this.setPartialState('password', value)}
                            value={this.state.password} />
                        </FormGroup>
                      </div>
                      <div style={styles.inputColumn}>
                        <FormGroup style={styles.inputGroup}>
                          <MaterialInput placeholder="First name"
                            required={true}
                            onChange={(value) => this.setPartialState('firstname', value)}
                            value={this.state.firstname} />
                        </FormGroup>
                      </div>
                      <div style={styles.inputColumn}>
                        <FormGroup style={styles.inputGroup}>
                          <MaterialInput placeholder="Last name"
                            required={true}
                            onChange={(value) => this.setPartialState('lastname', value)}
                            value={this.state.lastname} />
                        </FormGroup>
                      </div>
                    </div>

                    <FormGroup style={styles.inputGroup}>
                      <MaterialInput placeholder="Email"
                        required={true}
                        onChange={(value) => this.setPartialState('email', value)}
                        value={this.state.email} />
                    </FormGroup>
                    <FormGroup>
                      <Button type="submit" bsStyle="success" disabled={this.state.isSubmitting} style={styles.submitButton}>
                        <i className="fa fa-fw fa-user-plus" />
                        {this.state.isSubmitting ? 'Registering...' : 'Register'}
                      </Button>
                    </FormGroup>

                    {this.state.message ?
                      <FormGroup>
                        <Alert bsStyle="success" className="animated fadeIn">
                          {this.state.message}
                        </Alert>
                      </FormGroup>
                      : null
                    }

                    {this.state.error ?
                      <FormGroup>
                        <Alert bsStyle="danger" className="animated fadeIn">
                          <strong>
                            <i className="fa fa-fw fa-exclamation-circle" />
                            Uh oh! {this.state.error}
                          </strong>
                          <span>
                          </span>
                        </Alert>
                      </FormGroup>
                      : null
                    }
                  </form>

                  <div style={styles.footer}>
                    <span>Already have an account?&nbsp;</span>
                    <Link to="/login" style={styles.loginLink}>Sign in here</Link>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </section>
      </section>
    )
  }
}

export {
  Registration
}



// WEBPACK FOOTER //
// ./src/app/containers/Registration/index.tsx